/**
 * BB ID・内線番号のシステム内生成(発注者指示 2026-09-09 ②・⑧)。
 *   BB ID = 出勤形態×報酬区分×架電内容 の形態コード＋連番
 *     給湯器: 品川 SN＋1000〜 / 在宅時給 SR＋2000〜 / 在宅歩合 RS＋4000〜 / タイミー STM＋001〜
 *     不用品: 上のコード＋W(仮置き。システム設定で変更可)
 *   内線番号 = その連番の数字(SN1001 → 1001、STM001 → 001)
 *   番号は「設定の開始値」と「登録済みIDの最大値+1」の大きい方から続ける(CSVで移行した既存IDの続きになる)
 * ゆくゆくは API 連携で システム → BlueBean に自動反映する(Phase 5)。
 */
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import {
  BB_PREFIX_KEYS,
  DEFAULT_BB_NUMBERING,
  bbPrefixKey,
  type BbAccount,
  type BbNumberingSettings,
  type BbPrefixKey,
  type BbPrefixRule,
  type BbStyleKey,
  type BusinessType,
  type Operator,
  type PayType,
  type WorkStyle,
} from '@core/lib/types';

const SETTINGS_PATH = ['settings', 'bb_numbering'] as const;

/** 旧形式(出勤形態×報酬区分だけのキー)で保存された設定は給湯器のルールとして読む */
const LEGACY_KEYS: Record<string, BbPrefixKey> = {
  shinagawa: 'shinagawa_kyutoki',
  zaitaku_hourly: 'zaitaku_hourly_kyutoki',
  zaitaku_commission: 'zaitaku_commission_kyutoki',
  timee: 'timee_kyutoki',
  kanri: 'kanri_kyutoki',
};

export async function fetchBbNumbering(): Promise<BbNumberingSettings> {
  const snap = await getDoc(doc(getDb(), ...SETTINGS_PATH));
  const saved = ((snap.data() ?? {}) as { rules?: Record<string, BbPrefixRule> }).rules ?? {};
  const rules = { ...DEFAULT_BB_NUMBERING.rules };
  for (const [k, r] of Object.entries(saved)) {
    const key = (BB_PREFIX_KEYS as string[]).includes(k) ? (k as BbPrefixKey) : LEGACY_KEYS[k];
    if (key && r && typeof r.prefix === 'string') rules[key] = { prefix: r.prefix, start: Number(r.start) || 0, digits: Number(r.digits) || 1 };
  }
  return { rules };
}

export async function saveBbNumbering(settings: BbNumberingSettings, userId: string): Promise<void> {
  for (const [key, r] of Object.entries(settings.rules)) {
    if (!r.prefix.trim()) throw new Error(`${key} の形態コードを入力してください`);
    if (!Number.isInteger(r.start) || r.start < 0) throw new Error(`${key} の開始値は0以上の整数にしてください`);
    if (!Number.isInteger(r.digits) || r.digits < 1 || r.digits > 6) throw new Error(`${key} の桁数は1〜6にしてください`);
  }
  await setDoc(
    doc(getDb(), ...SETTINGS_PATH),
    { ...settings, updated_by: userId, updated_at: serverTimestamp() },
    { merge: true },
  );
}

/** 全オペレーターの使用済みBB ID(小文字で比較) */
export function usedBbIds(operators: Operator[]): Set<string> {
  const ids = new Set<string>();
  for (const o of operators) for (const a of o.bb_accounts) if (a.bb_id) ids.add(a.bb_id.toLowerCase());
  return ids;
}

/** タイミーは報酬区分が時給のみ */
export function normalizePayType(workStyle: WorkStyle, payType: PayType): PayType {
  return workStyle === 'timee' ? 'hourly' : payType;
}

/** 出勤形態×報酬区分の組 */
export function styleKeyFor(workStyle: WorkStyle, payType: PayType): BbStyleKey {
  switch (workStyle) {
    case 'shinagawa':
      return 'shinagawa';
    case 'timee':
      return 'timee';
    case 'kanri':
      return 'kanri';
    default:
      return normalizePayType(workStyle, payType) === 'commission' ? 'zaitaku_commission' : 'zaitaku_hourly';
  }
}

/** 出勤形態×報酬区分×架電内容から形態コードのキーを決める */
export function prefixKeyFor(workStyle: WorkStyle, payType: PayType, business: BusinessType): BbPrefixKey {
  return bbPrefixKey(styleKeyFor(workStyle, payType), business);
}

/** BB IDを生成する。used(小文字)は生成後に更新される */
export function generateBbId(workStyle: WorkStyle, payType: PayType, business: BusinessType, settings: BbNumberingSettings, used: Set<string>): string {
  const rule = settings.rules[prefixKeyFor(workStyle, payType, business)];
  const prefix = rule.prefix.trim();
  const re = new RegExp(`^${prefix.toLowerCase()}(\\d+)$`);
  let max = -1;
  for (const id of used) {
    const m = id.match(re);
    if (m) max = Math.max(max, Number(m[1]));
  }
  let n = Math.max(rule.start, max + 1);
  let candidate = `${prefix}${String(n).padStart(rule.digits, '0')}`;
  while (used.has(candidate.toLowerCase())) {
    n++;
    candidate = `${prefix}${String(n).padStart(rule.digits, '0')}`;
  }
  used.add(candidate.toLowerCase());
  return candidate;
}

/** 内線番号 = BB IDの連番の数字(SN1001 → 1001)。数字が無ければ null */
export function extensionOf(bbId: string): string | null {
  const m = bbId.trim().match(/(\d+)$/);
  return m ? m[1]! : null;
}

/**
 * 空欄のBB ID・内線番号を埋める(登録・CSV取込で共通)。
 * used は他のオペレーターの使用済み番号。この人の入力済み番号も先に予約してから生成する
 */
export function fillAccountNumbers(accounts: BbAccount[], settings: BbNumberingSettings, used: Set<string>): BbAccount[] {
  for (const a of accounts) if (a.bb_id.trim()) used.add(a.bb_id.trim().toLowerCase());
  return accounts.map((a) => {
    const payType = normalizePayType(a.work_style, a.pay_type);
    const bbId = a.bb_id.trim() || generateBbId(a.work_style, payType, a.business, settings, used);
    const ext = a.ext?.trim() || extensionOf(bbId);
    return { ...a, pay_type: payType, bb_id: bbId, ext };
  });
}
